// CB:BEGIN fr-014-realtor-quote-detail
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { getApiBaseUrl } from '../lib/getApiUrl';
import SharedHeaderRealtor from '../components/SharedHeaderRealtor';
import {
  ArrowLeft, FileText, Link2, CheckCircle, Clock, Users, Loader2,
  Copy, Mail, RotateCcw, X, AlertTriangle,
} from 'lucide-react';

const BRAND_GREEN = '#0f766e';

// ── Types ─────────────────────────────────────────────────────────────────────

const STATUS_META: Record<string, { label: string; bg: string; color: string; description: string }> = {
  COLLECTING_BIDS: { label: 'Collecting Bids', bg: '#dbeafe', color: '#1d4ed8', description: 'Contractors in the area can see this request and submit bids. You will be notified as bids come in.' },
  READY:           { label: 'Bids Ready',       bg: '#dcfce7', color: '#166534', description: 'The maximum number of bids has been received. Generate a client link to invite your client to review.' },
  INVITED:         { label: 'Client Invited',   bg: '#f0fdf4', color: '#166534', description: 'A portal link has been generated for your client. Track the invite status below.' },
  CONVERTED:       { label: 'Converted',        bg: '#dcfce7', color: '#166534', description: 'Your client selected a contractor. The project is now active.' },
  EXPIRED:         { label: 'Expired',           bg: '#f1f5f9', color: '#64748b', description: 'This quote request has expired. Create a new one if still needed.' },
};

const INVITE_STATUS: Record<string, { label: string; bg: string; color: string; hint: string }> = {
  ACTIVE:  { label: 'Link Generated',  bg: '#fef3c7', color: '#92400e', hint: 'Link created but email not yet sent.' },
  SENT:    { label: 'Email Sent',      bg: '#dbeafe', color: '#1d4ed8', hint: 'Email delivered to homeowner.' },
  OPENED:  { label: 'Link Viewed',     bg: '#f3e8ff', color: '#6d28d9', hint: 'Homeowner opened the portal link.' },
  CLAIMED: { label: 'Bid Accepted',    bg: '#dcfce7', color: '#166534', hint: 'Homeowner accepted a bid. Project created.' },
  EXPIRED: { label: 'Link Expired',    bg: '#f1f5f9', color: '#64748b', hint: 'This link has expired. Generate a new one.' },
  REVOKED: { label: 'Revoked',         bg: '#fee2e2', color: '#b91c1c', hint: 'Invite was cancelled.' },
};

type PortalInvite = {
  id: string;
  status: keyof typeof INVITE_STATUS;
  homeownerEmail: string;
  homeownerName: string | null;
  expiresAt: string;
  sentAt: string | null;
  openedAt: string | null;
  claimedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
};

type Bid = {
  id: string;
  amountCents?: number | null;
  scopeNotes?: string | null;
  warrantyNotes?: string | null;
  timelineDays?: number | null;
  contractor?: { company?: string | null } | null;
  createdAt?: string;
};

type BidFormData = {
  materialType?: string;
  materialManufacturer?: string;
  materialProductLine?: string;
  warrantyLaborYears?: number;
  warrantyMaterialYears?: number;
  estimatedDurationDays?: number;
  scopeNotes?: string;
  roofSizeSqFt?: number;
  availableColors?: Array<{ name: string }>;
};

function parseBidFormData(raw: string | null | undefined): BidFormData | null {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'object' && parsed !== null) return parsed as BidFormData;
  } catch {}
  return null;
}

const PLATFORM_FEE_RATE = 0.10;

type QuoteRequest = {
  id: string;
  propertyAddressLine1: string;
  propertyCity?: string | null;
  propertyState?: string | null;
  propertyZip?: string | null;
  status: string;
  maxBids?: number;
  expiresAt?: string | null;
  bids: Bid[];
};

// ── Helpers ───────────────────────────────────────────────────────────────────

function formatAddress(r: QuoteRequest): string {
  const parts = [r.propertyAddressLine1, r.propertyCity, r.propertyState, r.propertyZip].filter(Boolean);
  return parts.join(', ');
}

function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ' at ' + d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

// ── Component ─────────────────────────────────────────────────────────────────

const RealtorQuoteDetail: React.FC<{ quoteId: string; onNavigate: (screen: string) => void }> = ({
  quoteId,
  onNavigate,
}) => {
  const { getToken } = useAuth();
  const [request, setRequest] = useState<QuoteRequest | null>(null);
  const [loading, setLoading] = useState(true);

  // PDF
  const [pdfLoading, setPdfLoading] = useState(false);

  // Invite list
  const [invites, setInvites] = useState<PortalInvite[]>([]);
  const [invitesLoading, setInvitesLoading] = useState(false);

  // Generate invite form
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteName, setInviteName] = useState('');
  const [generateLoading, setGenerateLoading] = useState(false);

  // Per-invite action state
  const [emailLoading, setEmailLoading] = useState<string | null>(null);
  const [resendLoading, setResendLoading] = useState<string | null>(null);
  const [revokeLoading, setRevokeLoading] = useState<string | null>(null);
  const [confirmRevokeId, setConfirmRevokeId] = useState<string | null>(null);

  // Ephemeral portal URLs (cleared on page reload intentionally)
  const [portalUrls, setPortalUrls] = useState<Record<string, string>>({});
  const [copiedId, setCopiedId] = useState<string | null>(null);

  // Toast
  const [toast, setToast] = useState<{ msg: string; ok: boolean } | null>(null);

  const showToast = useCallback((msg: string, ok = true) => {
    setToast({ msg, ok });
    setTimeout(() => setToast(null), 4500);
  }, []);

  const getAuthHeaders = useCallback(async () => {
    const t = await getToken();
    return t ? { Authorization: `Bearer ${t}`, 'Content-Type': 'application/json' } : { 'Content-Type': 'application/json' };
  }, [getToken]);

  const base = getApiBaseUrl().replace(/\/$/, '');

  // ── Data fetchers ────────────────────────────────────────────────────────────

  const fetchDetail = useCallback(async () => {
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${base}/api/realtor/quotes/${quoteId}`, { headers });
      if (res.ok) {
        const data = await res.json();
        setRequest(data.request);
      }
    } catch {
      // non-fatal
    } finally {
      setLoading(false);
    }
  }, [quoteId, base, getAuthHeaders]);

  const fetchInvites = useCallback(async () => {
    setInvitesLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${base}/api/realtor/quotes/${quoteId}/portal-invites`, { headers });
      if (res.ok) {
        const data = await res.json();
        setInvites(data.invites || []);
      }
    } catch {
      // non-fatal
    } finally {
      setInvitesLoading(false);
    }
  }, [quoteId, base, getAuthHeaders]);

  useEffect(() => {
    if (quoteId) {
      fetchDetail();
      fetchInvites();
    }
  }, [quoteId, fetchDetail, fetchInvites]);

  // ── Actions ──────────────────────────────────────────────────────────────────

  const handleDownloadPdf = async () => {
    setPdfLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${base}/api/realtor/quotes/${quoteId}/pdf`, { method: 'POST', headers });
      if (res.ok) {
        const data = await res.json();
        if (data.downloadUrl) window.open(data.downloadUrl, '_blank', 'noopener');
        else showToast('PDF generated but no download URL returned', false);
      } else {
        showToast('Failed to generate PDF', false);
      }
    } catch {
      showToast('Failed to generate PDF', false);
    } finally {
      setPdfLoading(false);
    }
  };

  const handleGenerate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteEmail.trim() || !inviteEmail.includes('@')) {
      showToast('A valid homeowner email address is required.', false);
      return;
    }
    setGenerateLoading(true);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${base}/api/realtor/quotes/${quoteId}/portal-invite`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ homeownerEmail: inviteEmail.trim(), homeownerName: inviteName.trim() || undefined }),
      });
      if (res.ok) {
        const data = await res.json();
        setPortalUrls((prev) => ({ ...prev, [data.inviteId]: data.portalUrl }));
        setInviteEmail('');
        setInviteName('');
        await fetchInvites();
        await fetchDetail();
        showToast('Link generated. Copy it or email it to your client.');
      } else {
        const err = await res.json().catch(() => ({}));
        showToast(err.error || 'Failed to generate link', false);
      }
    } catch {
      showToast('Failed to generate link', false);
    } finally {
      setGenerateLoading(false);
    }
  };

  const handleCopy = async (inviteId: string) => {
    const url = portalUrls[inviteId];
    if (!url) {
      showToast('Link not available in this session. Use Resend Email to get a new link.', false);
      return;
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopiedId(inviteId);
      setTimeout(() => setCopiedId(null), 2500);
      showToast('Link copied to clipboard.');
    } catch {
      showToast('Copy failed — please copy the link manually.', false);
    }
  };

  const handleEmailInvite = async (inviteId: string) => {
    const url = portalUrls[inviteId];
    if (!url) {
      showToast('Link not available in this session. Use Resend Email to get a new link.', false);
      return;
    }
    setEmailLoading(inviteId);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${base}/api/realtor/portal-invites/${inviteId}/email`, {
        method: 'POST',
        headers,
        body: JSON.stringify({ portalUrl: url }),
      });
      if (res.ok) {
        showToast('Email sent to homeowner.');
        await fetchInvites();
      } else {
        const err = await res.json().catch(() => ({}));
        showToast(err.error || 'Failed to send email', false);
      }
    } catch {
      showToast('Failed to send email', false);
    } finally {
      setEmailLoading(null);
    }
  };

  const handleResend = async (inviteId: string) => {
    setResendLoading(inviteId);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${base}/api/realtor/portal-invites/${inviteId}/resend`, {
        method: 'POST',
        headers,
        body: JSON.stringify({}),
      });
      if (res.ok) {
        const data = await res.json();
        setPortalUrls((prev) => ({ ...prev, [inviteId]: data.portalUrl }));
        showToast('Email resent with a fresh link.');
        await fetchInvites();
      } else {
        const err = await res.json().catch(() => ({}));
        showToast(err.error || 'Failed to resend', false);
      }
    } catch {
      showToast('Failed to resend', false);
    } finally {
      setResendLoading(null);
    }
  };

  const handleRevoke = async (inviteId: string) => {
    setRevokeLoading(inviteId);
    try {
      const headers = await getAuthHeaders();
      const res = await fetch(`${base}/api/realtor/portal-invites/${inviteId}/revoke`, {
        method: 'POST',
        headers,
        body: JSON.stringify({}),
      });
      if (res.ok) {
        setConfirmRevokeId(null);
        setPortalUrls((prev) => { const n = { ...prev }; delete n[inviteId]; return n; });
        showToast('Invite revoked. You can generate a new link.');
        await fetchInvites();
        await fetchDetail();
      } else {
        const err = await res.json().catch(() => ({}));
        showToast(err.error || 'Failed to revoke invite', false);
      }
    } catch {
      showToast('Failed to revoke invite', false);
    } finally {
      setRevokeLoading(null);
    }
  };

  // ── Render helpers ───────────────────────────────────────────────────────────

  const activeInvite = invites.find((i) => ['ACTIVE', 'SENT', 'OPENED'].includes(i.status));
  const claimedInvite = invites.find((i) => i.status === 'CLAIMED');
  const previousInvites = invites.filter((i) => i !== activeInvite && i !== claimedInvite);

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
        <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', marginRight: 8 }} /> Loading…
        <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!request) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748b' }}>
        Quote not found.
      </div>
    );
  }

  const statusMeta = STATUS_META[request.status] ?? { label: request.status, bg: '#f1f5f9', color: '#475569', description: '' };
  const maxBids = request.maxBids ?? 3;
  const bidCount = request.bids.length;
  const quoteExpiresAt = request.expiresAt ? new Date(request.expiresAt) : null;
  const canGenerateInvite = bidCount > 0 && !['INVITED', 'CONVERTED', 'EXPIRED'].includes(request.status);

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: 'Inter, system-ui, -apple-system, Segoe UI, sans-serif' }}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed', top: 20, right: 20, zIndex: 9999, padding: '12px 18px', borderRadius: 10,
          background: toast.ok ? '#166534' : '#991b1b', color: '#fff', fontWeight: 600, fontSize: 14,
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)', maxWidth: 380,
        }}>
          {toast.msg}
        </div>
      )}

      {/* Revoke confirmation modal */}
      {confirmRevokeId && (
        <div
          onClick={() => setConfirmRevokeId(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.55)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
        >
          <div onClick={(e) => e.stopPropagation()} style={{ background: '#fff', borderRadius: 16, padding: 28, maxWidth: 380, width: '100%', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
              <AlertTriangle size={22} color="#b91c1c" />
              <span style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>Revoke this invite?</span>
            </div>
            <p style={{ fontSize: 14, color: '#475569', margin: '0 0 20px', lineHeight: 1.6 }}>
              The homeowner's link will stop working immediately. The quote request will return to <strong>Bids Ready</strong> so you can generate a new invite.
            </p>
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setConfirmRevokeId(null)}
                style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#64748b', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
              >
                Cancel
              </button>
              <button
                onClick={() => handleRevoke(confirmRevokeId!)}
                disabled={!!revokeLoading}
                style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', background: '#b91c1c', color: '#fff', fontSize: 14, fontWeight: 700, cursor: revokeLoading ? 'not-allowed' : 'pointer', opacity: revokeLoading ? 0.7 : 1 }}
              >
                {revokeLoading === confirmRevokeId ? 'Revoking…' : 'Revoke'}
              </button>
            </div>
          </div>
        </div>
      )}

      <SharedHeaderRealtor onNavigate={onNavigate} />

      <div style={{ maxWidth: 800, margin: '24px auto', padding: '0 24px', display: 'grid', gap: 20 }}>

        {/* Back + title */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <button
            onClick={() => onNavigate('realtor-dashboard')}
            style={{ background: 'none', border: '1px solid #cbd5e1', borderRadius: 8, padding: '6px 12px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, color: '#334155', fontSize: 14, fontWeight: 600 }}
          >
            <ArrowLeft size={16} /> Back
          </button>
          <div>
            <div style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>{formatAddress(request)}</div>
            <div style={{ fontSize: 13, color: '#64748b' }}>Quote request</div>
          </div>
        </div>

        {/* ── Status card ── */}
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 20 }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, marginBottom: 12 }}>
            <span style={{ padding: '4px 14px', borderRadius: 99, background: statusMeta.bg, color: statusMeta.color, fontSize: 13, fontWeight: 700 }}>
              {statusMeta.label}
            </span>
            {quoteExpiresAt && request.status === 'COLLECTING_BIDS' && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, color: '#64748b' }}>
                <Clock size={13} /> Expires {quoteExpiresAt.toLocaleDateString()}
              </span>
            )}
          </div>
          {statusMeta.description && (
            <p style={{ margin: 0, fontSize: 14, color: '#475569' }}>{statusMeta.description}</p>
          )}
          <div style={{ marginTop: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 6 }}>
                <Users size={14} /> Bids received
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: bidCount >= maxBids ? '#166534' : '#0f172a' }}>
                {bidCount} / {maxBids}
              </span>
            </div>
            <div style={{ height: 8, borderRadius: 99, background: '#f1f5f9', overflow: 'hidden' }}>
              <div style={{ height: '100%', borderRadius: 99, background: bidCount >= maxBids ? '#22c55e' : BRAND_GREEN, width: `${Math.min((bidCount / maxBids) * 100, 100)}%`, transition: 'width 300ms ease' }} />
            </div>
          </div>
        </div>

        {/* ── Client Portal Panel ── */}
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #f1f5f9', display: 'flex', alignItems: 'center', gap: 8 }}>
            <Link2 size={16} color={BRAND_GREEN} />
            <span style={{ fontWeight: 700, color: '#0f172a', fontSize: 15 }}>Client Portal Invite</span>
          </div>

          <div style={{ padding: 20 }}>

            {/* CLAIMED / CONVERTED */}
            {claimedInvite && (
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '14px 16px', background: '#f0fdf4', borderRadius: 12, border: '1px solid #bbf7d0' }}>
                <CheckCircle size={20} color="#16a34a" style={{ flexShrink: 0, marginTop: 2 }} />
                <div>
                  <div style={{ fontWeight: 700, color: '#166534', fontSize: 15, marginBottom: 4 }}>Bid accepted — project created</div>
                  <div style={{ fontSize: 13, color: '#166534' }}>
                    {claimedInvite.homeownerName || claimedInvite.homeownerEmail} accepted a bid on {formatDate(claimedInvite.claimedAt!)}.
                  </div>
                </div>
              </div>
            )}

            {/* No invite yet — show generate form */}
            {!activeInvite && !claimedInvite && (
              <>
                {bidCount === 0 ? (
                  <div style={{ textAlign: 'center', padding: '24px 0', color: '#94a3b8' }}>
                    <Users size={28} style={{ display: 'block', margin: '0 auto 10px', opacity: 0.4 }} />
                    <div style={{ fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Waiting for bids</div>
                    <div style={{ fontSize: 13 }}>You can generate a client link once at least one bid has been received.</div>
                  </div>
                ) : canGenerateInvite ? (
                  <form onSubmit={handleGenerate}>
                    <p style={{ margin: '0 0 14px', fontSize: 14, color: '#475569' }}>
                      Enter your client&apos;s contact info to generate a secure quote review link. You&apos;ll be able to copy or email the link after generating.
                    </p>
                    <div style={{ display: 'grid', gap: 10, marginBottom: 14 }}>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                          Homeowner Email <span style={{ color: '#ef4444' }}>*</span>
                        </label>
                        <input
                          type="email"
                          value={inviteEmail}
                          onChange={(e) => setInviteEmail(e.target.value)}
                          placeholder="client@example.com"
                          required
                          style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#0f172a', outline: 'none', boxSizing: 'border-box' }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#374151', marginBottom: 4 }}>
                          Homeowner Name <span style={{ color: '#94a3b8' }}>optional</span>
                        </label>
                        <input
                          type="text"
                          value={inviteName}
                          onChange={(e) => setInviteName(e.target.value)}
                          placeholder="Jane Smith"
                          style={{ width: '100%', padding: '9px 12px', border: '1.5px solid #e2e8f0', borderRadius: 8, fontSize: 14, color: '#0f172a', outline: 'none', boxSizing: 'border-box' }}
                        />
                      </div>
                    </div>
                    <button
                      type="submit"
                      disabled={generateLoading}
                      style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 20px', borderRadius: 10, border: 'none', background: BRAND_GREEN, color: '#fff', fontSize: 14, fontWeight: 700, cursor: generateLoading ? 'not-allowed' : 'pointer', opacity: generateLoading ? 0.7 : 1 }}
                    >
                      {generateLoading ? <Loader2 size={15} style={{ animation: 'spin 1s linear infinite' }} /> : <Link2 size={15} />}
                      {generateLoading ? 'Generating…' : 'Generate Link'}
                    </button>
                  </form>
                ) : null}
              </>
            )}

            {/* Active invite */}
            {activeInvite && (
              <InviteCard
                invite={activeInvite}
                portalUrl={portalUrls[activeInvite.id] ?? null}
                isCopied={copiedId === activeInvite.id}
                emailLoading={emailLoading === activeInvite.id}
                resendLoading={resendLoading === activeInvite.id}
                revokeConfirming={confirmRevokeId === activeInvite.id}
                onCopy={() => handleCopy(activeInvite.id)}
                onEmail={() => handleEmailInvite(activeInvite.id)}
                onResend={() => handleResend(activeInvite.id)}
                onRevoke={() => setConfirmRevokeId(activeInvite.id)}
              />
            )}

            {/* Previous invites */}
            {previousInvites.length > 0 && (
              <details style={{ marginTop: 16 }}>
                <summary style={{ fontSize: 12, color: '#94a3b8', cursor: 'pointer', fontWeight: 600, userSelect: 'none', padding: '4px 0' }}>
                  {previousInvites.length} previous invite{previousInvites.length !== 1 ? 's' : ''}
                </summary>
                <div style={{ marginTop: 10, display: 'grid', gap: 8 }}>
                  {previousInvites.map((inv) => (
                    <div key={inv.id} style={{ padding: '10px 14px', borderRadius: 10, border: '1px solid #f1f5f9', background: '#f8fafc', display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
                      <InviteStatusBadge status={inv.status} />
                      <span style={{ fontSize: 13, color: '#374151' }}>{inv.homeownerEmail}</span>
                      <span style={{ fontSize: 12, color: '#94a3b8' }}>Created {formatDate(inv.createdAt)}</span>
                      {inv.revokedAt && <span style={{ fontSize: 12, color: '#94a3b8' }}>Revoked {formatDate(inv.revokedAt)}</span>}
                      {inv.claimedAt && <span style={{ fontSize: 12, color: '#166534', fontWeight: 600 }}>Claimed {formatDate(inv.claimedAt)}</span>}
                    </div>
                  ))}
                </div>
              </details>
            )}

            {invitesLoading && (
              <div style={{ textAlign: 'center', padding: '12px 0', color: '#94a3b8', fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Loading invites…
              </div>
            )}
          </div>
        </div>

        {/* ── PDF Download ── */}
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 20 }}>
          <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>Quote Summary PDF</div>
          <button
            onClick={handleDownloadPdf}
            disabled={pdfLoading}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '10px 18px', borderRadius: 10, border: `1.5px solid ${BRAND_GREEN}`, background: '#fff', color: BRAND_GREEN, fontSize: 14, fontWeight: 600, cursor: pdfLoading ? 'not-allowed' : 'pointer', opacity: pdfLoading ? 0.7 : 1 }}
          >
            {pdfLoading ? <Loader2 size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <FileText size={16} />}
            {pdfLoading ? 'Generating…' : 'Download PDF'}
          </button>
        </div>

        {/* ── Bids ── */}
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 20 }}>
          <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 14 }}>Bids ({bidCount})</div>
          {bidCount === 0 ? (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#64748b' }}>
              <Users size={32} style={{ display: 'block', margin: '0 auto 12px', opacity: 0.4 }} />
              <div style={{ fontWeight: 600, marginBottom: 4 }}>No bids yet</div>
              <div style={{ fontSize: 13 }}>Contractors will submit bids as they review this request. Up to {maxBids} bids will be collected.</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 16 }}>
              {(() => {
                // Compute comparison badges across all bids
                const parsedAll = request.bids.map(b => ({ bid: b, fd: parseBidFormData(b.scopeNotes) }));
                const prices = parsedAll.map(({ bid: b }) => b.amountCents).filter((v): v is number => v != null);
                const minPrice = prices.length > 0 ? Math.min(...prices) : null;
                const warranties = parsedAll.map(({ fd }) => fd?.warrantyMaterialYears ?? 0);
                const maxWarranty = Math.max(...warranties);
                const durations = parsedAll.map(({ fd, bid: b }) => fd?.estimatedDurationDays ?? b.timelineDays ?? null);
                const validDurations = durations.filter((v): v is number => v != null);
                const minDuration = validDurations.length > 0 ? Math.min(...validDurations) : null;

                return request.bids.map((bid, i) => {
                  const fd = parseBidFormData(bid.scopeNotes);
                  const contractorTotal = bid.amountCents ?? null;
                  const platformFee = contractorTotal != null ? Math.round(contractorTotal * PLATFORM_FEE_RATE) : null;
                  const customerTotal = contractorTotal != null ? contractorTotal + platformFee! : null;
                  const scopeText = fd?.scopeNotes || (!fd && bid.scopeNotes ? bid.scopeNotes : null);
                  const isBestPrice = contractorTotal != null && minPrice != null && contractorTotal === minPrice && prices.length > 1;
                  const bidWarranty = fd?.warrantyMaterialYears ?? 0;
                  const isBestWarranty = bidWarranty > 0 && bidWarranty === maxWarranty && warranties.filter(w => w === maxWarranty).length === 1;
                  const bidDuration = fd?.estimatedDurationDays ?? bid.timelineDays ?? null;
                  const isFastest = bidDuration != null && minDuration != null && bidDuration === minDuration && validDurations.length > 1;
                  const badges = [
                    isBestPrice ? { label: 'Best Price', bg: '#dcfce7', color: '#166534' } : null,
                    isBestWarranty ? { label: 'Best Warranty', bg: '#eff6ff', color: '#1d4ed8' } : null,
                    isFastest ? { label: 'Fastest', bg: '#fef3c7', color: '#92400e' } : null,
                  ].filter(Boolean) as { label: string; bg: string; color: string }[];

                  return (
                    <div key={bid.id} style={{ borderRadius: 14, border: '1px solid #e2e8f0', background: '#fff', overflow: 'hidden' }}>
                    {/* Bid header */}
                    <div style={{ padding: '14px 18px', background: '#f0fdf4', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
                      <div>
                        <div style={{ fontWeight: 700, color: '#0f172a', fontSize: 15, marginBottom: badges.length > 0 ? 6 : 0 }}>
                          Quote {i + 1} — {bid.contractor?.company ?? 'Contractor'}
                        </div>
                        {badges.length > 0 && (
                          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                            {badges.map(b => (
                              <span key={b.label} style={{ padding: '2px 10px', borderRadius: 99, background: b.bg, color: b.color, fontSize: 11, fontWeight: 700 }}>{b.label}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        {customerTotal != null ? (
                          <>
                            <div style={{ fontSize: 22, fontWeight: 800, color: '#0f172a' }}>{formatCents(customerTotal)}</div>
                            <div style={{ fontSize: 11, color: '#64748b' }}>Customer price (incl. 10% platform fee)</div>
                          </>
                        ) : (
                          <div style={{ fontSize: 14, color: '#94a3b8' }}>Amount pending</div>
                        )}
                      </div>
                    </div>

                    {/* Details grid */}
                    <div style={{ padding: '14px 18px', display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '10px 20px' }}>
                      {fd?.materialType && (
                        <div>
                          <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Material</div>
                          <div style={{ fontSize: 14, color: '#0f172a' }}>{fd.materialType}</div>
                          {fd.materialManufacturer && <div style={{ fontSize: 12, color: '#64748b' }}>{fd.materialManufacturer}{fd.materialProductLine ? ` · ${fd.materialProductLine}` : ''}</div>}
                        </div>
                      )}
                      {(fd?.warrantyMaterialYears != null || fd?.warrantyLaborYears != null) && (
                        <div>
                          <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Warranty</div>
                          <div style={{ fontSize: 14, color: '#0f172a' }}>
                            {fd?.warrantyMaterialYears != null && <span>{fd.warrantyMaterialYears}yr material</span>}
                            {fd?.warrantyMaterialYears != null && fd?.warrantyLaborYears != null && ' · '}
                            {fd?.warrantyLaborYears != null && <span>{fd.warrantyLaborYears}yr labor</span>}
                          </div>
                        </div>
                      )}
                      {(fd?.estimatedDurationDays != null || bid.timelineDays != null) && (
                        <div>
                          <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Timeline</div>
                          <div style={{ fontSize: 14, color: '#0f172a' }}>{fd?.estimatedDurationDays ?? bid.timelineDays} days</div>
                        </div>
                      )}
                      {fd?.roofSizeSqFt != null && (
                        <div>
                          <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Roof Size</div>
                          <div style={{ fontSize: 14, color: '#0f172a' }}>{fd.roofSizeSqFt.toLocaleString()} sq ft</div>
                        </div>
                      )}
                      {contractorTotal != null && (
                        <div>
                          <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Contractor Bid</div>
                          <div style={{ fontSize: 14, color: '#0f172a' }}>{formatCents(contractorTotal)}</div>
                        </div>
                      )}
                      {bid.createdAt && (
                        <div>
                          <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Submitted</div>
                          <div style={{ fontSize: 14, color: '#0f172a' }}>{new Date(bid.createdAt).toLocaleDateString()}</div>
                        </div>
                      )}
                    </div>

                    {scopeText && (
                      <div style={{ padding: '0 18px 14px', borderTop: '1px solid #f1f5f9', paddingTop: 12 }}>
                        <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 4 }}>Scope of Work</div>
                        <div style={{ fontSize: 13, color: '#475569', lineHeight: 1.6 }}>{scopeText}</div>
                      </div>
                    )}
                  </div>
                  );
                });
              })()}
            </div>
          )}
        </div>

        {/* ── Quote details ── */}
        <div style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', padding: 20 }}>
          <div style={{ fontWeight: 700, color: '#0f172a', marginBottom: 12 }}>Quote Details</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            <div>
              <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Quote ID</div>
              <code style={{ fontSize: 12, color: '#475569' }}>{request.id}</code>
            </div>
            <div>
              <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Max Bids</div>
              <div style={{ fontSize: 14, color: '#475569' }}>{maxBids}</div>
            </div>
            {quoteExpiresAt && (
              <div>
                <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Expires</div>
                <div style={{ fontSize: 14, color: '#475569' }}>{quoteExpiresAt.toLocaleDateString()}</div>
              </div>
            )}
            <div>
              <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>Status</div>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 10px', borderRadius: 99, background: statusMeta.bg, color: statusMeta.color, fontSize: 12, fontWeight: 700 }}>
                {statusMeta.label}
              </span>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
};

// ── Sub-components ─────────────────────────────────────────────────────────────

function InviteStatusBadge({ status }: { status: string }) {
  const meta = INVITE_STATUS[status] ?? { label: status, bg: '#f1f5f9', color: '#64748b', hint: '' };
  return (
    <span style={{ padding: '3px 10px', borderRadius: 99, background: meta.bg, color: meta.color, fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
      {meta.label}
    </span>
  );
}

interface InviteCardProps {
  invite: PortalInvite;
  portalUrl: string | null;
  isCopied: boolean;
  emailLoading: boolean;
  resendLoading: boolean;
  revokeConfirming: boolean;
  onCopy: () => void;
  onEmail: () => void;
  onResend: () => void;
  onRevoke: () => void;
}

function InviteCard({
  invite, portalUrl, isCopied, emailLoading, resendLoading,
  onCopy, onEmail, onResend, onRevoke,
}: InviteCardProps) {
  const meta = INVITE_STATUS[invite.status] ?? INVITE_STATUS['ACTIVE'];
  const isActive = invite.status === 'ACTIVE';
  const isSentOrOpened = invite.status === 'SENT' || invite.status === 'OPENED';
  const canCopy = !!portalUrl;
  const canEmail = isActive && !!portalUrl;
  const canResend = isSentOrOpened;
  const canRevoke = isActive || isSentOrOpened;

  const expiresAt = new Date(invite.expiresAt);
  const isExpiringSoon = !isActive && expiresAt.getTime() - Date.now() < 24 * 60 * 60 * 1000;

  return (
    <div style={{ border: `1.5px solid ${isExpiringSoon ? '#fcd34d' : '#e2e8f0'}`, borderRadius: 12, padding: '16px 18px', background: isExpiringSoon ? '#fffbeb' : '#f8fafc' }}>
      {/* Status row */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10, marginBottom: 12 }}>
        <InviteStatusBadge status={invite.status} />
        <span style={{ fontSize: 13, color: '#374151', fontWeight: 600 }}>{invite.homeownerEmail}</span>
        {invite.homeownerName && <span style={{ fontSize: 13, color: '#64748b' }}>({invite.homeownerName})</span>}
      </div>

      {/* Hint */}
      <p style={{ margin: '0 0 10px', fontSize: 13, color: '#64748b' }}>{meta.hint}</p>

      {/* Timeline */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 6, marginBottom: 14 }}>
        <TimelineItem label="Created" value={invite.createdAt} />
        {invite.sentAt && <TimelineItem label="Email Sent" value={invite.sentAt} />}
        {invite.openedAt && <TimelineItem label="Link Viewed" value={invite.openedAt} />}
        <TimelineItem
          label="Link Expires"
          value={invite.expiresAt}
          highlight={isExpiringSoon ? '#b45309' : undefined}
        />
      </div>

      {/* Portal URL display (ephemeral — only available from generate/resend response) */}
      {portalUrl && (
        <div style={{ marginBottom: 14, padding: '10px 12px', background: '#f0fdf4', borderRadius: 8, border: '1px solid #bbf7d0' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: '#166534', marginBottom: 4 }}>Portal link</div>
          <code style={{ fontSize: 11, color: '#0f172a', wordBreak: 'break-all', lineHeight: 1.5 }}>{portalUrl}</code>
        </div>
      )}

      {/* Action buttons */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
        {canCopy && (
          <ActionButton
            icon={isCopied ? <CheckCircle size={14} /> : <Copy size={14} />}
            label={isCopied ? 'Copied!' : 'Copy Link'}
            onClick={onCopy}
            variant={isCopied ? 'success' : 'outline'}
          />
        )}
        {canEmail && (
          <ActionButton
            icon={emailLoading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Mail size={14} />}
            label={emailLoading ? 'Sending…' : 'Email Link'}
            onClick={onEmail}
            disabled={emailLoading}
            variant="primary"
          />
        )}
        {canResend && (
          <ActionButton
            icon={resendLoading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <RotateCcw size={14} />}
            label={resendLoading ? 'Resending…' : 'Resend Email'}
            onClick={onResend}
            disabled={resendLoading}
            variant="outline"
          />
        )}
        {canRevoke && (
          <ActionButton
            icon={<X size={14} />}
            label="Revoke"
            onClick={onRevoke}
            variant="danger"
          />
        )}
      </div>

      {!canCopy && !canEmail && !canResend && isActive && (
        <p style={{ margin: '10px 0 0', fontSize: 12, color: '#94a3b8' }}>
          Page refreshed — link is no longer available here. Use Resend Email to get a new link.
        </p>
      )}
    </div>
  );
}

function TimelineItem({ label, value, highlight }: { label: string; value: string; highlight?: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', marginBottom: 2 }}>{label}</div>
      <div style={{ fontSize: 13, color: highlight ?? '#374151', fontWeight: highlight ? 600 : 400 }}>
        {value ? new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—'}
      </div>
    </div>
  );
}

interface ActionButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean | null;
  variant: 'primary' | 'outline' | 'success' | 'danger';
}

function ActionButton({ icon, label, onClick, disabled, variant }: ActionButtonProps) {
  const styles: Record<string, React.CSSProperties> = {
    primary: { background: BRAND_GREEN, color: '#fff', border: 'none' },
    outline: { background: '#fff', color: '#334155', border: '1.5px solid #e2e8f0' },
    success: { background: '#f0fdf4', color: '#166534', border: '1.5px solid #bbf7d0' },
    danger:  { background: '#fff8f8', color: '#b91c1c', border: '1.5px solid #fecaca' },
  };
  return (
    <button
      onClick={onClick}
      disabled={!!disabled}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '8px 14px', borderRadius: 8, fontSize: 13, fontWeight: 600,
        cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.6 : 1,
        transition: 'opacity 0.15s',
        ...styles[variant],
      }}
    >
      {icon}{label}
    </button>
  );
}

export default RealtorQuoteDetail;
// CB:END fr-014-realtor-quote-detail
